import type { FrameSpec } from '../config/frames';
import { framePhoto } from '../catalogue/photos';
import { hasShape, lineName, shapesIn, type CatalogueLine } from '../config/collections';
import { btn, h, icon } from './icons';
import { rovingToolbar } from './roving';
import { words } from './words';

export interface FramesViewOptions {
  /** Collection shown in the header, e.g. « Handmade ». */
  line?: string;
  /** Handle of the product page the try-on was opened from (embedded only). */
  pageHandle?: string;
  /** Root used for product links: the shop root when embedded, maisonmata.com when standalone. */
  productRoot: string;
  /** Switch the model; resolves true once the new frame is on the face. */
  onSelect: (frame: FrameSpec) => void;
  /** « Voir <modèle> » for the page we are already on: just leave the try-on. */
  onCurrentPage?: () => void;
  /**
   * A42F « Tout voir »: URL of the catalogue page of the collection shown (A200: Handmade or
   * Signature), with this frame pre-selected when it belongs to it. No URL, no button.
   */
  catalogueHref?: (frame: FrameSpec, line: CatalogueLine | undefined) => string;
  /** Standalone: the catalogue is the page under the try-on, so « Tout voir » only has to close it. */
  onSeeAll?: (frame: FrameSpec, line: CatalogueLine | undefined) => void;
  /**
   * A200: the Handmade / Signature switch at the top of the list. The two collections are never
   * mixed: the switch replaces the list (the model on the face stays until another one is chosen).
   */
  collections?: { current: CatalogueLine; lists: Record<CatalogueLine, readonly FrameSpec[]>; onLine?: (line: CatalogueLine) => void };
}

/**
 * A42E « Modèles » view, A42F split CTA. Mobile: kept line, horizontal snap
 * row, then « Voir <modèle> → » and « Tout voir » side by side.
 * Desktop: left rail with « HANDMADE · 29 », vertical list, and the two
 * buttons stacked in the footer. Both buttons are always visible.
 */
export class FramesView {
  readonly root = h('div', 'mt-view mt-frames-view');
  private readonly kept = h('span', 'mt-kept-text');
  private readonly cards = new Map<string, HTMLButtonElement>();
  private readonly cta = h('a', 'mt-cta mt-product-link');
  private readonly ctaText = h('span', 'mt-cta-text');
  private readonly seeAll = h('a', 'mt-cta-secondary mt-see-all');
  private readonly count = h('span', 'mt-fv-count');
  private readonly list = h('div', 'mt-fv-list');
  private readonly shapes = h('div', 'mt-fv-shapes');
  /** One Tab stop for the shape chips (the one that is on). */
  private syncShapes: () => void = () => undefined;
  private readonly lineButtons = new Map<CatalogueLine, HTMLButtonElement>();
  private selected: FrameSpec;
  private frames: readonly FrameSpec[];
  private line: CatalogueLine | undefined;
  private shape = 'all';

  constructor(frames: readonly FrameSpec[], selectedId: string, private readonly opts: FramesViewOptions) {
    const w = words();
    this.frames = frames;
    this.line = opts.collections?.current;
    this.root.dataset.view = 'frames';
    this.root.setAttribute('role', 'group');
    this.root.setAttribute('aria-label', w.models);
    this.selected = frames.find(f => f.id === selectedId) ?? frames[0]!;

    const head = h('div', 'mt-fv-head');
    const kept = h('span', 'mt-kept');
    const check = icon('check'); check.classList.add('mt-kept-check');
    kept.append(check, this.kept);
    // A200: Handmade · Signature switch, then the shape filter — one row on phones (the panel stays low),
    // stacked in the desktop rail.
    const filters = h('div', 'mt-fv-filters');
    if (opts.collections) {
      this.root.classList.add('mt-frames-view--lines');
      const lines = h('div', 'mt-seg mt-fv-lines');
      lines.setAttribute('role', 'group');
      lines.setAttribute('aria-label', w.collections.switchLabel);
      for (const line of ['Handmade', 'Classic'] as const) {
        const list = opts.collections.lists[line];
        const b = btn('mt-seg-item mt-fv-line');
        b.dataset.line = line;
        b.append(h('span', 'mt-fv-line-name', lineName(line)), h('span', 'mt-fv-line-n', String(list.length)));
        b.setAttribute('aria-label', `${lineName(line)} · ${list.length}`);
        b.onclick = () => this.showLine(line);
        this.lineButtons.set(line, b);
        lines.append(b);
      }
      filters.append(lines);
    }
    this.shapes.setAttribute('aria-label', w.collections.shape);
    this.syncShapes = rovingToolbar(this.shapes, '.mt-shape-chip');
    filters.append(this.shapes);
    head.append(filters, this.count, kept);

    const foot = h('div', 'mt-fv-foot');
    this.cta.append(this.ctaText, icon('arrow'));
    this.cta.addEventListener('click', event => {
      if (this.selected.productHandle && this.selected.productHandle === opts.pageHandle && opts.onCurrentPage) {
        event.preventDefault();
        opts.onCurrentPage();
      }
    });
    // « Tout voir » (mobile) / « Voir tous les modèles » (desktop): same tab, current frame pre-selected.
    this.seeAll.append(icon('grid'), h('span', 'mt-short', w.seeAll), h('span', 'mt-long', w.seeAllFrames));
    this.seeAll.hidden = !opts.catalogueHref && !opts.onSeeAll;
    this.seeAll.addEventListener('click', event => {
      if (!opts.onSeeAll || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      opts.onSeeAll(this.selected, this.line);
    });
    foot.append(this.cta, this.seeAll);
    this.root.append(head, this.list, foot);
    this.renderList();
    this.setSelected(this.selected.id);
  }

  /** The collection shown in the list (A200), undefined without the switch. */
  get shownLine(): CatalogueLine | undefined { return this.line; }

  /** A200: shows the other collection (the shape filter starts again on « Toutes »). */
  showLine(line: CatalogueLine): void {
    const lists = this.opts.collections?.lists;
    if (!lists || line === this.line) return;
    this.line = line;
    this.frames = lists[line];
    this.shape = 'all';
    this.renderList();
    this.updateSeeAll();
    this.opts.collections?.onLine?.(line);
    this.list.scrollLeft = 0; this.list.scrollTop = 0;
  }

  /** Model cards of the collection shown, the shape chips present in it, the count. */
  private renderList(): void {
    const w = words();
    this.cards.clear();
    this.list.replaceChildren();
    for (const frame of this.frames) {
      const b = h('button', 'mt-model');
      b.type = 'button';
      b.dataset.frameId = frame.id;
      b.setAttribute('aria-label', `${frame.model} — ${frame.colorway}`);
      b.title = `${frame.model} — ${frame.colorway}`;
      const card = h('span', 'mt-model-card');
      const photo = framePhoto(frame);
      const img = h('img'); img.alt = ''; img.loading = 'lazy'; img.decoding = 'async'; img.src = photo.src;
      if (photo.normalised) img.classList.add('mt-model-img--norm');
      else if (frame.id === 'komodo-rise-olive') img.classList.add('mt-model-img--komodo');
      card.append(img);
      b.append(card, h('span', 'mt-model-name', frame.model));
      b.onclick = () => { if (frame.id !== this.selected.id) this.opts.onSelect(frame); };
      b.setAttribute('aria-pressed', String(frame.id === this.selected.id));
      this.cards.set(frame.id, b);
      this.list.append(b);
    }
    this.lineButtons.forEach((b, key) => b.setAttribute('aria-pressed', String(key === this.line)));
    // Shape chips: « Toutes » + the shapes of this collection (Shopify « Forme » field).
    this.shapes.replaceChildren();
    const present = shapesIn(this.frames);
    this.shapes.hidden = present.length < 2;
    for (const value of ['all', ...present]) {
      const chip = btn('mt-shape-chip', value === 'all' ? w.collections.allShapes : w.collections.shapes[value] ?? value);
      chip.dataset.shape = value;
      chip.onclick = () => this.filter(value);
      this.shapes.append(chip);
    }
    this.filter(this.shape);
  }

  /** A200: keeps only the models of a shape (« all »: every model of the collection). */
  filter(shape: string): void {
    this.shape = shape;
    let visible = 0;
    for (const frame of this.frames) {
      const on = hasShape(frame, shape);
      const card = this.cards.get(frame.id);
      if (card) card.hidden = !on;
      if (on) visible++;
    }
    this.shapes.querySelectorAll<HTMLButtonElement>('.mt-shape-chip').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.shape === shape)));
    if (!this.shapes.contains(document.activeElement)) this.syncShapes();
    const name = this.line ? lineName(this.line) : this.opts.line ?? 'Handmade';
    this.count.textContent = words().collection(name.toUpperCase(), visible);
  }

  private updateSeeAll(): void {
    const href = this.opts.catalogueHref?.(this.selected, this.line);
    if (href) this.seeAll.href = href; else this.seeAll.removeAttribute('href');
  }

  setKept(text: string): void { if (this.kept.textContent !== text) this.kept.textContent = text; }

  setSelected(id: string): void {
    const lists = this.opts.collections?.lists;
    const frame = this.frames.find(f => f.id === id) ?? (lists ? [...lists.Handmade, ...lists.Classic].find(f => f.id === id) : undefined);
    if (!frame) return;
    this.selected = frame;
    this.cards.forEach((b, key) => b.setAttribute('aria-pressed', String(key === id)));
    const handle = frame.productHandle;
    this.cta.hidden = !handle;
    if (handle) {
      const root = this.opts.productRoot.endsWith('/') ? this.opts.productRoot : `${this.opts.productRoot}/`;
      this.cta.href = `${root}products/${encodeURIComponent(handle)}`;
      const current = handle === this.opts.pageHandle;
      if (current) { this.cta.removeAttribute('target'); this.cta.removeAttribute('rel'); }
      else { this.cta.target = '_blank'; this.cta.rel = 'noopener'; }
      this.ctaText.textContent = words().view(frame.model);
    }
    this.updateSeeAll();
  }

  /** Bring the model being tried into view when the panel opens. */
  reveal(): void {
    const b = this.cards.get(this.selected.id);
    const list = b?.parentElement;
    if (!b || !list) return;
    // Scroll the list only (never the host page behind the fixed overlay).
    if (list.scrollWidth > list.clientWidth) list.scrollLeft = b.offsetLeft - (list.clientWidth - b.offsetWidth) / 2;
    if (list.scrollHeight > list.clientHeight) list.scrollTop = b.offsetTop - (list.clientHeight - b.offsetHeight) / 2;
  }
}
